// src/lib/contracts/render.js
// Wraps a contract body in HomeIQa letterhead + signature blocks + footer.
// The result is self-contained HTML — embeddable in an iframe for preview
// and renderable to PDF via puppeteer.

import { CONTRACT_META, bodyByType } from './templates';
import { CONTRACT_CSS } from './styles';
import { todayLong } from './format';

// Public entry point. Returns a complete <html>…</html> document.
export function renderContractHtml({ type, fields, documentId, parties, assetType }) {
  const meta = CONTRACT_META[type];
  if (!meta) throw new Error(`Unknown contract type: ${type}`);

  // Thread the asset type (property vs land) into the fields so templates can
  // render the right clauses, and adjust the PSA title for vacant land.
  const f = { ...(fields || {}), assetType: assetType || fields?.assetType || 'property' };
  const isLand = f.assetType === 'land';
  const docTitle = (type === 'psa' && isLand)
    ? 'CONTRACT FOR THE SALE & PURCHASE OF VACANT LAND'
    : meta.docTitle;

  const body = bodyByType(type, f);
  const sigParties = (parties && parties.length ? parties : meta.signers);

  return `<!DOCTYPE html>
<html lang="en"><head>
<meta charset="utf-8">
<title>${esc(meta.label)} · HomeIQa</title>
<style>${CONTRACT_CSS}</style>
</head><body>
<div class="page">
  ${letterhead({ docId: documentId, generatedOn: todayLong() })}
  <h1 class="doc-title">${esc(docTitle)}</h1>
  <div class="doc-subtitle">${esc(meta.subtitle)}</div>
  <div class="body">${body}</div>
  ${signatureBlocks(sigParties)}
  ${footer({ docId: documentId, label: meta.label })}
</div>
</body></html>`;
}

// TLJ monogram — burgundy badge, gold serif initials with the L in
// lighter gold. Kept as a compact inline SVG so it prints crisp and
// carries no external asset dependency.
const TLJ_MONOGRAM_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" style="width:60px;height:60px;display:inline-block;">
  <circle cx="50" cy="50" r="47" fill="#4A1220"/>
  <circle cx="50" cy="50" r="47" fill="none" stroke="#C9A84C" stroke-width="1.5"/>
  <text x="50" y="63" font-family="Georgia, 'Times New Roman', serif" font-size="32" font-weight="600" text-anchor="middle" letter-spacing="1" fill="#C9A84C">T<tspan fill="#E2C47A">L</tspan>J</text>
</svg>`;

function letterhead({ docId, generatedOn }) {
  return `
    <header class="letterhead">
      <div class="lh-meta">
        <div><span class="lh-label">Generated</span>${esc(generatedOn)}</div>
        ${docId ? `<div><span class="lh-label">Document ID</span><span style="font-family:monospace;">${esc(docId)}</span></div>` : ''}
      </div>
      <div style="text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
        ${TLJ_MONOGRAM_SVG}
        <div class="lh-tag">Confidential</div>
      </div>
    </header>
  `;
}

// Signature blocks — one per party, two-column grid
function signatureBlocks(parties) {
  const blocks = parties.map(p => `
    <div class="sigblock">
      <div class="sig-area" data-role="${esc(p.role)}"></div>
      <div class="sig-label">${esc(p.label || p.role || 'Signer')}</div>
      <div class="sig-name">${esc(p.name || '')}</div>
      <div class="sig-date">${p.signedAt
        ? 'Signed ' + esc(p.signedAt)
        : 'Date:<span class="date-line"></span>'}</div>
    </div>
  `).join('');
  return `<div class="sigs">${blocks}</div>`;
}

function footer({ docId, label }) {
  return `
    <footer class="doc-footer">
      <span><span class="secure">ESIGN Act compliant</span> · ${esc(label || '')}</span>
      <span class="docid">${esc(docId || '')}</span>
    </footer>
  `;
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Sample data so the agent can preview each template even without a real lead
export function sampleFields(type) {
  const today = new Date().toISOString().slice(0, 10);
  const closing = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const accept  = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const common = {
    sellerName: 'Jane M. Doe',
    sellerAddress: '2847 Thornwood Lane, Atlanta, GA 30305',
    buyerName: 'Towanjalin L. Jones',
    subjectProperty: '2847 Thornwood Lane, Atlanta, GA 30305',
    legalDescription: 'See legal description in the title commitment attached hereto.',
    purchasePrice: 175000,
    acceptanceDeadline: accept,
    closingDate: closing
  };
  switch (type) {
    case 'psa': return common;
    case 'assignment': return {
      ...common,
      assignmentDate: today,
      assignorName: 'Towanjalin L. Jones',
      assignorAddress: '1900 Peachtree Rd NE, Atlanta, GA 30309',
      assigneeName: 'Pinetree Capital Partners LLC',
      assigneeAddress: '450 Mansell Rd, Roswell, GA 30076',
      earnestMoneyAmount: 1000,
      titleCompany: 'Atlas Title & Escrow, LLC',
      specialProvisions: ''
    };
    case 'novation': return {
      sellerName: 'Jane M. Doe',
      attorneyInFactName: 'Towanjalin L. Jones',
      subjectProperty: '2847 Thornwood Lane, Atlanta, GA 30305'
    };
    case 'cancellation': return {
      cancellationDate: today,
      subjectProperty: '2847 Thornwood Lane, Atlanta, GA 30305',
      buyerName: 'Towanjalin L. Jones',
      sellerName: 'Jane M. Doe',
      reasonForTermination: 'Title defects could not be cured within the agreed cure period.',
      specialProvisions: '',
      executionDate: today
    };
    case 'jv': return {
      jvDate: today,
      signorName: 'Towanjalin L. Jones',
      partnerName: 'Pinetree Capital Partners LLC',
      splitLabel: '50/50',
      signorPct: 50,
      partnerPct: 50,
      propertyAddress: '2847 Thornwood Lane, Atlanta, GA 30305',
      additionalConditions: ''
    };
    case 'amendment': return {
      originalAgreementDate: new Date(Date.now() - 10 * 86400000).toISOString().slice(0, 10),
      sellerName: 'Jane M. Doe',
      buyerName: 'Towanjalin L. Jones and/or assigns',
      subjectProperty: '2847 Thornwood Lane, Atlanta, GA 30305',
      consentingParties: 'The Parties',
      amendmentText: 'Section 3 (Purchase Price) is amended to read: The total purchase price shall be $172,500.00 (One Hundred Seventy-Two Thousand Five Hundred and 00/100 Dollars), payable at closing by wire transfer.\n\nSection 5 (Closing Date) is amended to extend the closing date to September 10, 2026.'
    };
    default: return {};
  }
}
